import React, { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight, Clock, Image as ImageIcon, Star } from "lucide-react";
import Seo from "../components/site/Seo";
import { PHONE_DISPLAY, PHONE_TEL } from "../lib/site";
import { track } from "../lib/analytics";

const NEXT_STEPS = [
  { icon: Phone, title: "Tyler Will Call You", desc: "Tyler personally reviews every request and will reach out shortly to talk through your project." },
  { icon: Clock, title: "We Schedule a Visit", desc: "We'll find a time that works for you and come take a look at your concrete in person." },
  { icon: CheckCircle2, title: "You Get an Honest Quote", desc: "A clear, written estimate with no pressure and no surprises. Never cutting corners." },
];

const ThankYou = () => {
  const location = useLocation();
  const fromForm = location.state?.fromForm;

  useEffect(() => {
    if (fromForm) track("lead_thank_you_view", { service: location.state?.service, city: location.state?.city });
  }, [fromForm, location.state]);

  // Guard: this page is only reachable right after a successful submission.
  if (!fromForm) return <Navigate to="/contact" replace />;

  const firstName = (location.state?.name || "").trim().split(" ")[0];

  return (
    <div>
      <Seo title="Thank You | PRYME EPOXY" description="Thank you for your estimate request. Tyler will be in touch shortly." path="/thank-you" />

      <section className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-5 lg:px-12 text-center">
          <div className="w-20 h-20 mx-auto bg-[#C69453]/10 border border-[#C69453]/40 flex items-center justify-center text-[#C69453]" data-testid="thank-you-check">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <span className="block mt-8 text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Request Received</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-3 leading-tight">
            {firstName ? `Thank You, ${firstName}!` : "Thank You!"}
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl mt-5 leading-relaxed">
            Your free estimate request is in. Tyler will personally follow up soon — usually the same day.
            Prefer to talk now? Give him a call.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <a href={PHONE_TEL} data-testid="thank-you-call" className="bg-[#C69453] hover:bg-[#AE7F44] text-black font-extrabold text-lg px-8 py-4 transition-colors flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" /><span>Call Pryme: {PHONE_DISPLAY}</span>
            </a>
            <Link to="/gallery" className="border border-[#C69453] text-[#C69453] hover:bg-[#C69453]/10 font-bold text-lg px-8 py-4 transition-all flex items-center justify-center gap-2">
              <ImageIcon className="w-5 h-5" /><span>Browse Our Work</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">What Happens Next</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {NEXT_STEPS.map((s, i) => (
              <div key={s.title} className="border border-zinc-800 bg-[#141414] p-7 relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />
                <span className="text-[#C69453] font-heading text-2xl font-bold">{String(i + 1).padStart(2, "0")}</span>
                <div className="w-11 h-11 mt-4 bg-[#C69453]/10 border border-[#C69453]/30 flex items-center justify-center text-[#C69453]">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white mt-4">{s.title}</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-12 text-center flex-wrap">
            <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}</div>
            <p className="text-zinc-300">Rated <strong>5.0 stars</strong> by 69 Northern Arizona homeowners</p>
          </div>

          <div className="text-center mt-10">
            <Link to="/" className="inline-flex items-center gap-2 text-[#C69453] font-semibold hover:gap-3 transition-all">
              <span>Back to Home</span><ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
